import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

//Components
import { WpForm, WpFormLayout } from 'Wpds/components/form';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import { VisuallyHidden } from 'Wpds/components/visually-hidden/VisuallyHidden';
import { WpRadioGroup, WpRadioInput } from 'Wpds/components/radio';

//Selectors
import * as WorkspaceTemplatesSelectors from 'Shared/selectors/WorkspaceTemplates.selectors';

//Actions
import * as WorkspaceTemplatesActions from 'Shared/actions/WorkspaceTemplates.actions';

type Props = {
  onToggle(): void;
  templateSlug: string;
};

function SaveWorkspaceTemplateModal({ onToggle, templateSlug }: Props) {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitButtonRef = useRef(null);

  const handleCancelClick = () => onToggle();

  const template = useSelector(state =>
    WorkspaceTemplatesSelectors.getWorkspaceTemplateBySlug(state, { slug: templateSlug })
  ).toJS();

  const handleSubmitButtonClick = () => {
    submitButtonRef.current.click();
  };

  const handleSubmit = values => {
    setIsSubmitting(true);
    dispatch(
      WorkspaceTemplatesActions.updateWorkspaceTemplate(template.slug, {
        name: values.template_name,
        isPublic: values.template_privacy === 'public',
        description: values.template_description,
      })
    ).then(() => {
      setIsSubmitting(false);
      onToggle();
    });
  };

  return (
    <WpModal isOpen size="md" onToggle={onToggle}>
      <WpModalHeader>Edit template</WpModalHeader>
      <WpModalBody>
        <WpForm id="update_workspace_template_modal_wp_form_wrapper" onSubmit={handleSubmit}>
          <WpFormLayout>
            <WpTextField required id="template_name" label="Template name" value={template.name} />
            <WpTextField id="template_description" label="Description" value={template.description} />
            <WpRadioGroup
              id="template_privacy"
              label="Where do you want to save this template?"
              defaultValue={template.isPublic ? 'public' : 'private'}
            >
              <WpRadioInput
                id="private"
                label="Private Gallery"
                value="private"
                descriptionText="Only you will be able to see and reuse this template"
              />
              <WpRadioInput
                id="public"
                label="Public Gallery"
                value="public"
                descriptionText="Everyone at your company will be able to see and reuse this template"
              />
            </WpRadioGroup>
          </WpFormLayout>
          <VisuallyHidden>
            <WpButton btnType="submit" ref={submitButtonRef}>
              submit
            </WpButton>
          </VisuallyHidden>
        </WpForm>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={handleCancelClick}>
            Cancel
          </WpButton>
          <WpButton onClick={handleSubmitButtonClick} loading={isSubmitting}>
            Save
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
}

export default SaveWorkspaceTemplateModal;
